import { ApiClientError, describeApiError } from 'grabbitbot-legacy-ui'

/**
 * Plain-words UI text for every machine error code the GrabbitBot backend
 * (api.grabbitbot.com, plus the spotbot / payment passthroughs it relays)
 * emits. The message says what happened AND what to do next. The generic
 * transport/status wording (network failure, 401, 5xx, unknown code) lives in
 * the UI lib's `describeApiError`; only GrabbitBot-specific codes live here.
 *
 * Sources: api.grabbitbot.com app/main.py exception handlers, app/routers/*,
 * app/payments/*, app/jobs/*, app/spotbot_proxy.py; spotbot.api.grabbitbot.com
 * app/router.py + admin.py. Keep in step when the backend adds a code.
 */
export const GRABBITBOT_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // --- payments -----------------------------------------------------------
  payments_disabled: 'Payments are not switched on yet, so nothing can be charged. Nothing was charged; please try again later.',
  payment_service_not_configured: 'The payment service is not set up yet. Nothing was charged; please try again later or contact support.',
  payment_service_error: 'The payment service could not process this. Please check Transactions before trying again, or contact support.',
  // Honest error contract (api.grabbitbot.com 0.27.0): the three named failure classes of the payment service.
  payment_auth_failed:
    'The payment service did not accept this request, so it was not processed. Nothing was charged; please try again later or contact support.',
  payment_auth_unavailable: 'Payments are temporarily unavailable. Nothing was charged; please try again in a minute.',
  payment_service_unavailable:
    'The payment service could not be reached. If you were paying, check Transactions before trying again — do not pay twice.',
  stripe_not_configured: 'Card payments are not set up yet. Nothing was charged; please try again later.',
  invalid_idempotency_key: 'The payment request was not valid. Nothing was charged; reload the page and try again.',
  idempotency_key_reused: 'The payment request was refused (it did not match the earlier attempt). Nothing new was charged.',
  request_in_progress: 'This payment is still being processed. Wait a minute, then check Transactions before trying again.',
  charge_outcome_unknown: 'Outcome unknown — do not retry, contact support. This payment may or may not have been charged.',
  payment_method_not_set: 'No card is saved yet. Add a payment method first, then try again.',
  paymentmethod_expired: 'Your saved card has expired. Add a new payment method, then try again.',
  subscription_not_found: 'You have no subscription yet. Choose a plan and pay first.',
  subscription_not_active: 'Your subscription is not active. Renew it first, then try again.',
  cant_expire: 'This checkout session can no longer be cancelled.',
  no_plan_list: 'No plan is selected yet. Choose and pay for a plan first.',
  no_credit_balances: 'There is no credit yet. Top up your credit first, then start the bot.',
  no_product_list: 'No product is active on this account. Buy a product first.',
  plan_not_found: 'That plan was not found.',
  replaced_plan_not_running:
    'The plan you chose to replace is no longer running. Nothing was charged; reload the page and choose again.',
  fully_covered_use_startonline:
    'Your balances cover this payment in full, so no card is needed and nothing was charged. Go to Payment → Subscriptions, press "Subscribe" (or "Switch from …") on the plan, then "Pay from balance".',
  invalid_replaces_plan: 'The plan to replace was not valid. Nothing was charged; reload the page and try again.',
  invalid_plans: 'That plan was not valid. Reload the page and try again.',
  quote_unavailable: 'A payment preview is not available yet.',
  // --- bot control --------------------------------------------------------
  exchange_already_connected:
    'You already have a connection to this exchange. An exchange can only be connected once.',
  connection_not_found: 'This connection no longer exists.',
  bot_running: 'This bot still has coins with trading switched on. Turn trading off for every coin of this connection first.',
  bot_run_in_progress: 'The bot is running right now. Try again in a minute.',
  connection_has_open_positions: 'This connection still has open positions. Close or delete them first.',
  connection_has_open_orders:
    'This connection still has open orders on the exchange. Wait for them to finish or cancel them first.',
  bot_halted: 'This bot was halted after an order problem. Contact support before deleting it.',
  bot_action_in_progress: 'Another action is already running for this bot — please wait for it to finish.',
  nonactive_bot_status: 'The bot is not running. Start the bot first, then try again.',
  no_amount: 'Enter an amount before opening a position.',
  spotbot_not_configured: 'The bot is not configured for this exchange yet.',
  spotbot_unreachable: 'Outcome unknown — check positions before retrying.',
  exchange_not_allowed: 'Trading on this exchange is not enabled on this system yet. Choose another exchange.',
  coin_pair_not_allowed:
    'This coin pair cannot be traded yet. Choose one of the coin pairs offered on the Coins page; selling what you already hold still works.',
  engine_not_ported: 'This bot function is not available on this system yet.',
  database_unavailable: 'The bot service is temporarily unavailable. Please try again in a minute.',
  bot_v2_is_deprecated: 'Bot V2 is no longer available. Use the current bot settings instead.',
  exchangeinterface_not_configured: 'Exchange data is not available on this system yet.',
  exchangeinterface_unreachable: 'Exchange data is temporarily unavailable. Please try again in a minute.',
  candle_unreachable: 'Price data is temporarily unavailable. Please try again in a minute.',
  // --- account / sign-in --------------------------------------------------
  identity_not_configured: 'Your account is not fully set up yet. Please contact support.',
  moved_to_auth0: 'Password sign-in has been replaced. Use the sign-in button instead.',
  user_banned: 'This account is blocked. Please contact support.',
  email_not_verified: 'Your e-mail address is not verified with the sign-in provider. Verify it, then sign in again.',
  no_email: 'The sign-in provider did not share an e-mail address. Use another sign-in method or contact support.',
  oauth_failed: 'Signing in failed. Please try again.',
  gone: 'This item no longer exists.',
  validation_error: 'Some of the values are not valid. Please check the form and try again.',
  // --- admin --------------------------------------------------------------
  credit_change_in_progress: 'Another credit change for this user + currency is already in progress.',
  user_has_no_serviceId: 'This user has no service id yet.',
  user_not_found: 'No such user.',
  email_not_found: 'No user with that e-mail address.',
  email_not_set: 'Enter an e-mail first.',
  process_log_not_available: 'Process logs are not available yet for this backend.',
  // --- scheduled jobs (admin-triggered) -----------------------------------
  job_running: 'This job is already running. Wait for it to finish.',
  job_failed: 'The job failed. Check the process log.',
  job_retired: 'This job has been retired and no longer runs.',
  renew_already_attempted: 'A renewal was already attempted for this period.',
}

/** One sentence for any failed call: GrabbitBot code table first, then the
 * UI lib's generic wording. `fallback` = what the user was doing, without a
 * full stop ("Could not save the settings"). */
export function describeError(err: unknown, fallback?: string): string {
  // A plain Error is an internal failure, never prose meant for the user
  // (TypeError = fetch failed = network, which the lib names).
  if (!(err instanceof ApiClientError) && !(err instanceof TypeError)) {
    return `${fallback ?? 'The request failed'}.`
  }
  return describeApiError(err, { codes: GRABBITBOT_ERROR_MESSAGES, fallback })
}

/** "<what>. <reason>" for inline load/save failures, without repeating
 * `what` when the reason already starts with it (the no-information case). */
export function failureText(err: unknown, what: string): string {
  const text = describeError(err, what)
  return text.startsWith(what) ? text : `${what}. ${text}`
}
